import { useTranslations } from 'next-intl';
import type { LinkNavigationMenuItemProps } from '../../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../../NavigationMenu/NavigationMenu';
import type { SearchResult, SearchType } from '../../types';

type AllProductsResultsProps = {
  results: SearchResult[];
  searchTerm: string;
  searchType: SearchType;
  onResultClick: (result: SearchResult) => void;
};

function GetHighlightedText({ text, highlight }: { text: string; highlight: string }) {
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} className="bg-accent-yellow">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </span>
  );
}

/**
 * AllProductsResults component that displays the search results for the given search all type. It will display the appropriate results for the given search type.
 *
 * @param results - The results to display
 * @param searchTerm - The search term to display in the results
 * @param searchType - The type of search to perform (all, lotteries, games, syndicates, scratchcards)
 * @param onResultClick - The function to call when a result is clicked
 * @returns A NavigationMenu with the search results for the given search type
 */
function AllProductsResults({ results, searchTerm, searchType, onResultClick }: AllProductsResultsProps) {
  const t = useTranslations('general.search');

  const items: LinkNavigationMenuItemProps[] = results.map(result => {
    const normalizedName = result.name.trim().toLowerCase().replace(/\s+/g, '-');
    return {
      ...(searchType === 'all' ? { label: t(`product_types.${result.type}`) } : null),
      variant: 'link',
      data: <GetHighlightedText text={result.name} highlight={searchTerm} />,
      reversedLabelData: true,
      onClick: () => onResultClick(result),
      'data-testid': `search-result-${normalizedName}`,
      'data-track-name': result.name,
    };
  });

  return <NavigationMenu items={items} />;
}

export { AllProductsResults };
